<script setup lang="ts">
import MoreMenuItem from "@/utils/UIConstants/MoreMenuItem.ts";
import MoreMenuSearchField from "@/components/inputs/MoreMenuSearchField.vue";
import EmptyBoxIcon from "@/components/icons/navBarIcons/EmptyBoxIcon.vue";
import {Dialog, QItem, QList, QPopupProxy} from "quasar";
import {computed, PropType, ref} from "vue";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import MoreIcon from "@/assets/editViewIcons/MoreIcon.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import ShowFieldInfoDialog from "@/components/dialogs/ShowFieldInfoDialog.vue";
import EditUiElementPropertiesBtn from "@/components/EditView/EditUIElementPropertiesBtn.vue";
import Translator from "@/utils/common/Translator.ts";
import NaMaMetaData = namaTypes.NaMaMetaData;

let props = defineProps({
  metadata: {type: Object as PropType<NaMaMetaData>},
  moreMenuItems: {type: Object as PropType<MoreMenuItem[]>}
});
const {showFieldInfoActive} = storeToRefs(useNamaFlagsStore());
const searchValue = ref<string | null>(null);
const moreSelectedItem = ref(0);
const moreListRef = ref<QList | null>(null);
const moreListItemsRef = ref<QItem[] | null>(null);
const popupRef = ref<QPopupProxy | null>(null);

const displayedMenuItems = computed(() => {
  const items = props.moreMenuItems?.map(i => {
    if (!i.actionInfo)
      i.actionInfo = {title: {}}
    return i;
  })

  let value = searchValue.value;
  if (value) {
    if (!items)
      return [];
    return items.filter(l =>
      l.searchValue.toLowerCase().includes(value.toLowerCase()) ||
      l.searchValue.toLowerCase().includes(StringUtils.normalizeArabic(value)) ||
      l.searchValue.toLowerCase().includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(value))) ||
      l.searchValue.toLowerCase().includes(StringUtils.arabicToEnglish(value).toLowerCase()) ||
      l.seperator
    ) as MoreMenuItem[];
  }
  return items;
});

function moreMenuArrowDownKey() {
  moveSelection(1);
}

function moreMenuArrowUpKey() {
  moveSelection(-1);
}

function moveSelection(increment: number) {
  let currentItems = displayedMenuItems.value! as MoreMenuItem[];
  if (!currentItems.length)
    return;
  if (currentItems.filter(e => e.seperator).length == currentItems.length)
    return;
  moreSelectedItem.value += increment;

  if (moreSelectedItem.value <= 0) {
    moreSelectedItem.value = currentItems.length - 1;
  } else if (moreSelectedItem.value >= currentItems.length) {
    moreSelectedItem.value = 0;
  }
  if (currentItems[moreSelectedItem.value].seperator) {
    moveSelection(increment);
    return;
  }
  let currentItemsWithoutSeparators = currentItems.filter(e => !e.seperator);
  scrollToMoreMenuSelectedItem(currentItemsWithoutSeparators.indexOf(currentItems[moreSelectedItem.value]));
}

function scrollToMoreMenuSelectedItem(index: number) {
  if (!moreListRef.value || !moreListItemsRef.value)
    return;
  let item = moreListItemsRef.value[index];
  if (!item)
    return;
  item.$el.scrollIntoView({
    behavior: 'smooth', // Smooth scrolling animation
    block: 'nearest',   // Scroll to the nearest edge (can also use 'start', 'center', 'end')
    inline: 'nearest'   // Horizontal alignment (optional)
  });
}

function onClick() {
  if (displayedMenuItems.value) {
    displayedMenuItems.value[moreSelectedItem.value].callGUIAction(displayedMenuItems.value[moreSelectedItem.value] as MoreMenuItem, props.metadata!);
    if (popupRef.value)
      popupRef.value.hide();
  }
}


function focusSearchField(id: string) {
  document.getElementById(id)?.focus();
}

function onHide() {
  searchValue.value = '';
  moreSelectedItem.value = 0;
}

function showPopup() {
  popupRef.value!.show();
}

defineExpose({showPopup});

function showFieldInfoIfNeeded(e: MouseEvent, item: MoreMenuItem) {
  if (!showFieldInfoActive.value)
    return;
  if (!item.actionInfo)
    return;
  e.preventDefault()
  Dialog.create({
    component: ShowFieldInfoDialog,
    componentProps: {
      action: item.actionInfo,
      entityType: props.metadata?.owner?.ownerId,
    }
  });
}
</script>

<template>
  <MoreIcon class="tw-size-[22px] ultra:tw-size-5" />
  <q-popup-proxy class="tw-min-w-80 tw-min-h-80 tw-overflow-hidden tw-flex tw-flex-col dark:tw-bg-namaBackground-bright-dark tw-bg-namaBackground-bright-light dark:tw-text-white"
                 @hide="onHide" @show="focusSearchField('moremenuSearchInput')" ref="popupRef">
    <MoreMenuSearchField @keydown.enter="onClick" @keydown.down="moreMenuArrowDownKey" @keydown.up="moreMenuArrowUpKey"
                         v-model="searchValue" id="moremenuSearchInput"/>
    <q-list id="listview-more-menu" dense style="min-width: 100px" class="tw-overflow-auto tw-grow"
            v-if="displayedMenuItems && displayedMenuItems.length && !displayedMenuItems.every(l=>l.seperator)" ref="moreListRef">
      <template :key="index" v-for="(item, index) in displayedMenuItems">
        <q-item v-if="!item.seperator" clickable v-close-popup
                @click.stop="() => {item.callGUIAction(item as MoreMenuItem, metadata!);}"
                :class="{'tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white nama-icon-active':index === moreSelectedItem}"
                ref="moreListItemsRef" @contextmenu="(e)=>showFieldInfoIfNeeded(e,item)">
          <q-item-section>
            <div class="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2 tw-flex-nowrap">
              <NamaIcon :block="item.actionInfo" default-icon="nm-actions"/>
              <span v-text="Translator.translateNaMaText(item.actionInfo?.title,metadata?.owner?.ownerId) || item.text"/>
              <span class="tw-grow"/>
              <EditUiElementPropertiesBtn :block-or-field="item.actionInfo" :entity-type="metadata?.owner?.ownerId"/>
            </div>
          </q-item-section>
        </q-item>
        <q-separator
          v-else-if="item.seperator && index!==0 && index !== displayedMenuItems.length-1 && !displayedMenuItems[index-1].seperator"
          class="dark:tw-bg-[#6C757D]"/>
      </template>
    </q-list>
    <template v-else>
      <div class="tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-2.5 tw-grow">
        <EmptyBoxIcon width="50%" height="150px"/>
      </div>
    </template>
  </q-popup-proxy>
</template>
